import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { savePermitForm, updatePermitForm } from '../../store/slices/projectPermitsSlice';
import { showToast } from '../utils/Toast';
import { fetchProjectData, prefillFormData } from '../../utils/projectDataUtils';

const RoofPanelCutListForm = ({ projectId, currentPermit, onClose }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  
  // Define all the form fields based on the image
  const [formData, setFormData] = useState({
    customer: 'JRT ROOFING',
    job: '',
    contact: '',
    cellPhone: '',
    poNumber: '',
    jobAddress: '',
    date: '',
    jobNumber: '',
    panelProfile: '',
    width: '',
    gauge: '',
    color: '',
    flatOption: true, // Default option
    additionalOptions: '',
    cutList: Array(40).fill().map((_, i) => ({
      id: i + 1,
      quantity: '',
      length: '',
      squareFt: '',
      section: ''
    })),
    specialNotes: '',
    totalSquareFt: 0,
    totalLinearFt: 0
  });

  useEffect(() => {
    if (currentPermit && currentPermit.formType === 'roofPanelCutList') {
      setFormData(currentPermit.formData || formData);
    } else {
      // Fetch project data to prefill the form
      const getProjectData = async () => {
        try {
          const projectData = await fetchProjectData(projectId);
          const prefilled = prefillFormData(projectData, formData);
          setFormData(prefilled);
        } catch (error) {
          console.error('Error prefilling form data:', error);
        }
      };
      
      getProjectData();
    }
  }, [currentPermit, projectId]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCutListChange = (index, field, value) => {
    setFormData(prev => {
      const newCutList = [...prev.cutList];
      newCutList[index] = {
        ...newCutList[index],
        [field]: value
      };
      return {
        ...prev,
        cutList: newCutList
      };
    });
  };

  // Calculate totals whenever cutList changes
  const calculateTotals = useCallback(() => {
    let totalSquareFt = 0;
    let totalLinearFt = 0;

    formData.cutList.forEach(item => {
      const squareFt = parseFloat(item.squareFt) || 0;
      const length = parseFloat(item.length) || 0;
      const quantity = parseFloat(item.quantity) || 0;

      totalSquareFt += squareFt * quantity;
      totalLinearFt += length * quantity;
    });

    setFormData(prev => ({
      ...prev,
      totalSquareFt,
      totalLinearFt
    }));
  }, [formData.cutList]);

  useEffect(() => {
    calculateTotals();
  }, [formData.cutList, calculateTotals]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (currentPermit && currentPermit._id) {
        await dispatch(updatePermitForm({
          projectId,
          permitId: currentPermit._id,
          formData: {
            ...currentPermit,
            formData
          }
        })).unwrap();
        showToast('Roof Panel Cut List updated successfully', 'success');
      } else {
        await dispatch(savePermitForm({
          projectId,
          formType: 'roofPanelCutList',
          formData
        })).unwrap();
        showToast('Roof Panel Cut List saved successfully', 'success');
      }
      onClose();
    } catch (error) {
      showToast(error.message || 'Failed to save Roof Panel Cut List', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="permit-form-shell bg-white p-6 rounded-lg max-w-7xl mx-auto">
      {/* Logo and header section - commented out for now */}
      {/* 
      <div className="flex items-center justify-center mb-6">
        <img 
          src="/path-to-taylor-metal-products-logo.jpg" 
          alt="Taylor Metal Products" 
          className="h-16 mr-4"
        />
      </div>
      */}
      
      <h1 className="text-2xl font-bold mb-2 text-center">TAYLOR METAL PRODUCTS</h1>
      <h2 className="text-xl font-bold mb-6 text-center underline">Panel Cut List</h2>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="space-y-4">
            <div className="flex">
              <label className="w-32 font-bold">Customer:</label>
              <input
                type="text"
                value={formData.customer}
                onChange={(e) => handleChange('customer', e.target.value)}
                className="flex-1 border border-gray-300 p-1"
              />
            </div>
            <div className="flex">
              <label className="w-32 font-bold">Job:</label>
              <input
                type="text"
                value={formData.job}
                onChange={(e) => handleChange('job', e.target.value)}
                className="flex-1 border border-gray-300 p-1"
              />
            </div>
            <div className="flex">
              <label className="w-32 font-bold">Contact:</label>
              <input
                type="text"
                value={formData.contact}
                onChange={(e) => handleChange('contact', e.target.value)}
                className="flex-1 border border-gray-300 p-1"
              />
            </div>
            <div className="flex">
              <label className="w-32 font-bold">Cell Phone #:</label>
              <input
                type="text"
                value={formData.cellPhone}
                onChange={(e) => handleChange('cellPhone', e.target.value)}
                className="flex-1 border border-gray-300 p-1"
              />
            </div>
            <div className="flex">
              <label className="w-32 font-bold">PO #:</label>
              <input
                type="text"
                value={formData.poNumber}
                onChange={(e) => handleChange('poNumber', e.target.value)}
                className="flex-1 border border-gray-300 p-1"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex">
              <label className="w-32 font-bold">Job Address:</label>
              <input
                type="text"
                value={formData.jobAddress}
                onChange={(e) => handleChange('jobAddress', e.target.value)}
                className="flex-1 border border-gray-300 p-1"
              />
            </div>
            <div className="flex">
              <label className="w-32 font-bold">Date:</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                className="flex-1 border border-gray-300 p-1"
              />
            </div>
            <div className="flex">
              <label className="w-32 font-bold">Job Number:</label>
              <input
                type="text"
                value={formData.jobNumber}
                onChange={(e) => handleChange('jobNumber', e.target.value)}
                className="flex-1 border border-gray-300 p-1"
              />
            </div>
            <div className="text-right italic mt-4">
              <span className="font-bold">CHECK ALL THAT APPLY</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-6 border-t border-b border-gray-300 py-3">
          <div className="flex items-center">
            <label className="font-bold underline mr-2">PANEL PROFILE:</label>
            <select
              value={formData.panelProfile}
              onChange={(e) => handleChange('panelProfile', e.target.value)}
              className="flex-1 border border-gray-300 p-1"
            >
              <option value="">Select One</option>
              <option value="profile1">Profile 1</option>
              <option value="profile2">Profile 2</option>
              <option value="profile3">Profile 3</option>
            </select>
          </div>
          <div className="flex items-center">
            <label className="font-bold mr-2">WIDTH:</label>
            <input
              type="text"
              value={formData.width}
              onChange={(e) => handleChange('width', e.target.value)}
              className="w-16 border border-gray-300 p-1 mr-1"
            />
            <span>"</span>
          </div>
          <div className="flex items-center">
            <label className="font-bold mr-2">GAUGE:</label>
            <input
              type="text"
              value={formData.gauge}
              onChange={(e) => handleChange('gauge', e.target.value)}
              className="flex-1 border border-gray-300 p-1"
            />
          </div>
          <div className="flex items-center">
            <label className="font-bold mr-2">COLOR:</label>
            <select
              value={formData.color}
              onChange={(e) => handleChange('color', e.target.value)}
              className="flex-1 border border-gray-300 p-1"
            >
              <option value="">Select One</option>
              <option value="color1">Color 1</option>
              <option value="color2">Color 2</option>
              <option value="color3">Color 3</option>
            </select>
          </div>
        </div>

        <div className="mb-6">
          <div className="text-center font-bold mb-2">PLEASE CHECK ONE OPTION BELOW!!!</div>
          <div className="border border-gray-300 p-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.flatOption}
                onChange={(e) => handleChange('flatOption', e.target.checked)}
                className="mr-2"
              />
              <span className="font-bold">Flat</span>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 mb-6">
          <div className="col-span-3">
            <h2 className="text-xl font-bold text-center mb-4">CUT LIST</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="border border-gray-300 p-1 w-10 text-center">#</th>
                      <th className="border border-gray-300 p-1 text-center">QUANTITY</th>
                      <th className="border border-gray-300 p-1 text-center">LENGTH</th>
                      <th className="border border-gray-300 p-1 text-center">SQUARE FT.</th>
                      <th className="border border-gray-300 p-1 text-center">SECTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.cutList.slice(0, 20).map((item, index) => (
                      <tr key={index}>
                        <td className="border border-gray-300 p-1 text-center">{item.id}</td>
                        <td className="border border-gray-300 p-1">
                          <input
                            type="text"
                            value={item.quantity}
                            onChange={(e) => handleCutListChange(index, 'quantity', e.target.value)}
                            className="w-full p-0.5 border-0"
                          />
                        </td>
                        <td className="border border-gray-300 p-1">
                          <input
                            type="text"
                            value={item.length}
                            onChange={(e) => handleCutListChange(index, 'length', e.target.value)}
                            className="w-full p-0.5 border-0"
                          />
                        </td>
                        <td className="border border-gray-300 p-1">
                          <input
                            type="text"
                            value={item.squareFt}
                            onChange={(e) => handleCutListChange(index, 'squareFt', e.target.value)}
                            className="w-full p-0.5 border-0"
                          />
                        </td>
                        <td className="border border-gray-300 p-1">
                          <input
                            type="text"
                            value={item.section}
                            onChange={(e) => handleCutListChange(index, 'section', e.target.value)}
                            className="w-full p-0.5 border-0"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="border border-gray-300 p-1 w-10 text-center">#</th>
                      <th className="border border-gray-300 p-1 text-center">QUANTITY</th>
                      <th className="border border-gray-300 p-1 text-center">LENGTH</th>
                      <th className="border border-gray-300 p-1 text-center">SQUARE FT.</th>
                      <th className="border border-gray-300 p-1 text-center">SECTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.cutList.slice(20, 40).map((item, index) => (
                      <tr key={index + 20}>
                        <td className="border border-gray-300 p-1 text-center">{item.id}</td>
                        <td className="border border-gray-300 p-1">
                          <input
                            type="text"
                            value={item.quantity}
                            onChange={(e) => handleCutListChange(index + 20, 'quantity', e.target.value)}
                            className="w-full p-0.5 border-0"
                          />
                        </td>
                        <td className="border border-gray-300 p-1">
                          <input
                            type="text"
                            value={item.length}
                            onChange={(e) => handleCutListChange(index + 20, 'length', e.target.value)}
                            className="w-full p-0.5 border-0"
                          />
                        </td>
                        <td className="border border-gray-300 p-1">
                          <input
                            type="text"
                            value={item.squareFt}
                            onChange={(e) => handleCutListChange(index + 20, 'squareFt', e.target.value)}
                            className="w-full p-0.5 border-0"
                          />
                        </td>
                        <td className="border border-gray-300 p-1">
                          <input
                            type="text"
                            value={item.section}
                            onChange={(e) => handleCutListChange(index + 20, 'section', e.target.value)}
                            className="w-full p-0.5 border-0"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="md:pl-4">
            <div className="border border-gray-300 p-2 mb-4">
              <div className="font-bold text-center mb-2">ADDITIONAL OPTIONS:</div>
              <textarea
                value={formData.additionalOptions}
                onChange={(e) => handleChange('additionalOptions', e.target.value)}
                className="w-full p-1 border border-gray-300"
                rows="5"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <div className="font-bold mb-2">Special Notes / Accessories:</div>
            <textarea
              value={formData.specialNotes}
              onChange={(e) => handleChange('specialNotes', e.target.value)}
              className="w-full p-2 border border-gray-300"
              rows="6"
            />
          </div>
          <div>
            <div className="flex justify-between items-center mb-2">
              <div className="font-bold">TOTAL SQUARE FT:</div>
              <div className="font-bold">{formData.totalSquareFt.toFixed(2)}</div>
            </div>
            <div className="flex justify-between items-center mb-2">
              <div className="font-bold">TOTAL LINEAR FT:</div>
              <div className="font-bold">{formData.totalLinearFt.toFixed(2)}</div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Saving...' : currentPermit && currentPermit._id ? 'Update' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RoofPanelCutListForm;
